import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

// Iter313: Kontierungsregeln (Zweck → Konto, eigene Stichwortregeln) + Spesen-Erstattungsmodus
export function BookingRulesManager({ companyId }) {
  const [data, setData] = useState(null);
  const [rules, setRules] = useState([]);
  const [mode, setMode] = useState("payroll");
  const [draft, setDraft] = useState({ label: "", keywords: "", account: "", asset_account: "" });
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);

  const load = useCallback(async () => {
    const r = await apiClient.get(`/companies/${companyId}/booking-rules`);
    setData(r.data);
    setRules(r.data.rules || []);
    setMode(r.data.expense_reimbursement_mode || "payroll");
    try { const pr = await apiClient.get(`/companies/${companyId}/expense-policy`); setPolicy(pr.data); } catch { /* ignore */ }
  }, [companyId]);

  const savePolicy = async () => {
    try {
      const r = await apiClient.put(`/companies/${companyId}/expense-policy`, { km_rate: Number(policy.km_rate), meal_lunch: Number(policy.meal_lunch), meal_dinner: Number(policy.meal_dinner), lump_sum_account: policy.lump_sum_account });
      setPolicy(r.data);
      toast.success("Spesenreglement gespeichert");
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  useEffect(() => { if (companyId) load().catch(() => {}); }, [companyId, load]);

  const save = async (nextRules, nextMode) => {
    setSaving(true);
    try {
      const r = await apiClient.put(`/companies/${companyId}/booking-rules`, { rules: nextRules, expense_reimbursement_mode: nextMode });
      setData(r.data); setRules(r.data.rules || []); setMode(r.data.expense_reimbursement_mode);
      toast.success("Kontierungsregeln gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const setPurposeAccount = (key, account) => {
    const others = rules.filter((r) => r.purpose_key !== key);
    const next = account ? [...others, { purpose_key: key, account, label: data.defaults.find((d) => d.key === key)?.label }] : others;
    setRules(next);
  };

  const addCustom = () => {
    if (!draft.account || !draft.keywords.trim()) { toast.error("Konto und mindestens ein Stichwort angeben"); return; }
    const next = [...rules, { id: crypto.randomUUID(), label: draft.label || draft.account, account: draft.account.trim(),
      asset_account: draft.asset_account.trim() || null, keywords: draft.keywords.split(",").map((k) => k.trim()).filter(Boolean) }];
    setRules(next);
    setDraft({ label: "", keywords: "", account: "", asset_account: "" });
    save(next, mode);
  };

  if (!data) return null;
  const custom = rules.filter((r) => !r.purpose_key);
  const overrideFor = (key) => rules.find((r) => r.purpose_key === key)?.account || "";

  return (
    <div className="space-y-4" data-testid="booking-rules-manager">
      <p className="text-xs text-slate-500">
        Feste Regeln kontieren Belege deterministisch (Zweck beim Upload oder Stichwort in Notiz/Belegtext). Die KI ist nur Fallback.
        Ab CHF 1'000 wird bei Möbel/IT automatisch das Anlagekonto verwendet.
      </p>

      <div>
        <h3 className="text-xs font-semibold text-slate-700 mb-1">Standard-Zwecke («Wofür?») – Konto anpassen</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {data.purposes.filter((p) => p.key !== "other").map((p) => (
            <div key={p.key} className="flex items-center justify-between gap-2 text-xs border border-slate-100 rounded-md px-2 py-1" data-testid={`booking-rule-purpose-${p.key}`}>
              <span className="text-slate-700 truncate" title={(p.keywords || []).join(", ")}>{p.label}</span>
              <Input
                className="h-7 w-24 text-xs font-mono"
                value={overrideFor(p.key)}
                placeholder={data.defaults.find((d) => d.key === p.key)?.account || ""}
                onChange={(e) => setPurposeAccount(p.key, e.target.value.trim())}
                data-testid={`booking-rule-purpose-account-${p.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold text-slate-700 mb-1">Eigene Stichwortregeln</h3>
        {custom.length === 0 && <p className="text-xs text-slate-400">Noch keine eigenen Regeln.</p>}
        {custom.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-2 text-xs border border-slate-100 rounded-md px-2 py-1 mb-1" data-testid={`booking-rule-custom-${r.id}`}>
            <span className="truncate"><b>{r.label}</b> · {(r.keywords || []).join(", ")} → <span className="font-mono">{r.account}</span>{r.asset_account ? ` (≥ 1'000: ${r.asset_account})` : ""}</span>
            <Button variant="ghost" size="sm" className="h-7 text-rose-700" onClick={() => { const n = rules.filter((x) => x.id !== r.id); setRules(n); save(n, mode); }} data-testid={`booking-rule-delete-${r.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <div className="grid grid-cols-2 md:grid-cols-[1fr_2fr_90px_90px_auto] gap-1.5 mt-2">
          <Input className="h-8 text-xs" placeholder="Bezeichnung" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} data-testid="booking-rule-new-label" />
          <Input className="h-8 text-xs" placeholder="Stichwörter, kommagetrennt (z.B. kaffee, kapseln)" value={draft.keywords} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} data-testid="booking-rule-new-keywords" />
          <Input className="h-8 text-xs font-mono" placeholder="Konto" value={draft.account} onChange={(e) => setDraft({ ...draft, account: e.target.value })} data-testid="booking-rule-new-account" />
          <Input className="h-8 text-xs font-mono" placeholder="Anlage ≥1000" value={draft.asset_account} onChange={(e) => setDraft({ ...draft, asset_account: e.target.value })} data-testid="booking-rule-new-asset-account" />
          <Button size="sm" className="h-8" onClick={addCustom} disabled={saving} data-testid="booking-rule-add-button"><Plus className="h-3.5 w-3.5 mr-1" />Regel</Button>
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold text-slate-700 mb-1">Spesen-Rückerstattung an Mitarbeitende</h3>
        <div className="flex flex-wrap gap-3 text-xs">
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name="expmode" checked={mode === "payroll"} onChange={() => setMode("payroll")} data-testid="expense-mode-payroll" /> mit dem nächsten Lohn (AHV-frei, Ziff. 13.1.1)</label>
          <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name="expmode" checked={mode === "bank"} onChange={() => setMode("bank")} data-testid="expense-mode-bank" /> separat per Bank (2060 an Bank)</label>
        </div>
      </div>

      {policy && (
        <div data-testid="expense-policy-section">
          <h3 className="text-xs font-semibold text-slate-700 mb-1">Spesenreglement – Pauschalen ohne Beleg (gelten als effektive Spesen, Ziff. 13.1.1)</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5 items-end text-xs">
            <label>CHF / km<Input className="h-8 text-xs" type="number" step="0.05" value={policy.km_rate} onChange={(e) => setPolicy({ ...policy, km_rate: e.target.value })} data-testid="expense-policy-km-rate" /></label>
            <label>Mittagessen<Input className="h-8 text-xs" type="number" step="0.5" value={policy.meal_lunch} onChange={(e) => setPolicy({ ...policy, meal_lunch: e.target.value })} data-testid="expense-policy-meal-lunch" /></label>
            <label>Abendessen<Input className="h-8 text-xs" type="number" step="0.5" value={policy.meal_dinner} onChange={(e) => setPolicy({ ...policy, meal_dinner: e.target.value })} data-testid="expense-policy-meal-dinner" /></label>
            <label>Aufwandkonto<Input className="h-8 text-xs font-mono" value={policy.lump_sum_account} onChange={(e) => setPolicy({ ...policy, lump_sum_account: e.target.value })} data-testid="expense-policy-account" /></label>
            <Button size="sm" variant="outline" className="h-8" onClick={savePolicy} data-testid="expense-policy-save-button">Reglement speichern</Button>
          </div>
        </div>
      )}

      <Button size="sm" onClick={() => save(rules, mode)} disabled={saving} className="bg-slate-900 text-white" data-testid="booking-rules-save-button">
        {saving ? "Speichert…" : "Kontierungsregeln speichern"}
      </Button>
    </div>
  );
}
